import React, { useCallback, useEffect, memo, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useUFramework } from '../../context';
import { FILE } from '../../common/constant';
import { getThumbor, downloadFile } from '../../common/function';
import PropTypes from 'prop-types';
import * as Icon from 'ubs-icons';

// Import components
import FilePreviewHeader from './components/file-preview-header';
import ImagePreview from './components/image-preview';
import VideoPreview from './components/video-preview';
import UFileDoc from './components/file-doc';
import OtherFile from './components/other-file';
import TextPreview from './components/text-preview';
import HtmlPreview from './components/html-preview';
import CodePreview from './components/code-preview';
import { getFrameworkFileIcon } from '..';

// Main file preview component
const UFilePreview = memo((props) => {
  const { files, initialIndex, onClose, onDelete, options, actions, subheader, hideDownload, hideFileInfo } = props;
  const [activeIndex, setActiveIndex] = useState(initialIndex || 0);
  const [rotation, setRotation] = useState(0);
  const activeThumbRef = useRef(null);
  const previewRef = useRef(null);

  const currentFile = files && files[activeIndex] ? files[activeIndex] : props.file;

  const _S3_BASE_URL = useUFramework(e => e?.S3_BASE_URL);
  const fileUrl = currentFile?.url ? ((currentFile.url.startsWith('blob:') || currentFile.url.startsWith('data:') || currentFile.url.startsWith('http')) ? currentFile.url : (_S3_BASE_URL + currentFile.url)) : '';

  const handleDownload = useCallback((e) => {
    if (e && e.stopPropagation) { e.stopPropagation(); e.preventDefault(); }
    downloadFile(currentFile?.base64 || fileUrl, currentFile?.fileName);
  }, [fileUrl, currentFile?.fileName, currentFile?.base64]);

  const handleRotate = useCallback((direction = 'right') => {
    if (direction === 'right') {
      setRotation(prev => prev + 90);
    } else {
      setRotation(prev => prev - 90);
    }
  }, []);

  useEffect(() => {
    setRotation(0);
  }, [activeIndex]);

  // Chromium refuses to navigate an <iframe> to a data: URL larger than ~2MB, so a large
  // base64 PDF (e.g. one carrying header/footer/background images) renders as a blank
  // preview. Decode the base64 PDF into a short-lived blob: URL — a reference with no size
  // limit — and render that instead. Falls back to the original data URI if decoding ever
  // fails, so non-PDF files and edge cases behave exactly as before.
  const [pdfObjectUrl, setPdfObjectUrl] = useState('');
  useEffect(() => {
    const b64 = currentFile?.base64;
    if (!b64 || !b64.includes('application/pdf')) { setPdfObjectUrl(''); return undefined; }
    let objectUrl = '';
    try {
      const commaIdx = b64.indexOf(',');
      if (commaIdx !== -1 && /;base64/i.test(b64.slice(0, commaIdx))) {
        const binary = atob(b64.slice(commaIdx + 1));
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      }
    } catch (e) {
      objectUrl = '';
    }
    setPdfObjectUrl(objectUrl);
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [currentFile?.base64]);

  const getExt = (url) => {
    if (!url) return '';
    return url.split(/[#?]/)[0].split('.').pop().trim().toLowerCase();
  };

  const renderPreviewContent = () => {
    const ext = getExt(currentFile?.url);

    if (!currentFile?.url && !currentFile?.base64) {
      return <OtherFile file={currentFile} onDownload={handleDownload} />;
    }

    if (currentFile?.base64) {
      if (currentFile?.base64?.includes('application/pdf')) {
        return <iframe src={(pdfObjectUrl || currentFile.base64) + "#toolbar=0&navpanes=0&embedded=true"} type="application/pdf" width="100%" height="100%" frameBorder="0" title="PDF Preview" />
      } else if (currentFile?.base64?.includes('text/html')) {
        return <div className="u_fv-html-preview"><iframe src={currentFile.base64} width="100%" height="100%" frameBorder="0" title="HTML Preview" /></div>
      } else {
        return <OtherFile file={currentFile} onDownload={handleDownload}/>;
      }
    } else if (FILE.IMAGES.test(`.${ext}`)) {
      return <ImagePreview src={fileUrl} alt={currentFile?.fileName} rotation={rotation} />;
    } else if (FILE.VIDEO.test(`.${ext}`)) {
      return <VideoPreview src={fileUrl} />;
    } else if (FILE.AUDIO.test(`.${ext}`)) {
      return (
          <div className="u_fv-audio u_m-auto">
              <Icon.Music size={64} fill="#FFF" className="u_m-auto u_mb-20 u_block"/>
              <audio controls src={fileUrl} className="u_fv-audio-player">
                  Your browser does not support the audio element.
              </audio>
          </div>
      );
    } else if (FILE.DOCSFILES.test(`.${ext}`)) {
      return <UFileDoc url={fileUrl} type={ext} onDownload={handleDownload} />;
    } else if (FILE.ELSXFILES.test(`.${ext}`)) {
      return <UFileDoc url={fileUrl} type={ext} onDownload={handleDownload} />;
    } else if (FILE.PPTFILES.test(`.${ext}`)) {
      return <UFileDoc url={fileUrl} type={ext} onDownload={handleDownload} />;
    } else if (FILE.PDFFILES.test(`.${ext}`)) {
      return <UFileDoc url={fileUrl} type={ext} onDownload={handleDownload} rotation={rotation} />;
    } else if (FILE.HTMLFILES.test(`.${ext}`)) {
      return <HtmlPreview src={fileUrl} fileName={currentFile?.fileName} />;
    } else if (FILE.CODEFILES.test(`.${ext}`)) {
      return <CodePreview src={fileUrl} fileName={currentFile?.fileName} />;
    } else if (FILE.TEXTFILES.test(`.${ext}`)) {
      return <TextPreview src={fileUrl} fileName={currentFile?.fileName} />;
    } else return <OtherFile file={currentFile} onDownload={handleDownload}/>;
  };

  // Re-focus preview when mouse moves over non-iframe areas (header, overlay, arrows)
  // Lets iframe buttons (zoom/share) work, Esc works when mouse is outside iframe
  const handleMouseOver = useCallback((e) => {
    if (e.target?.tagName !== 'IFRAME' && previewRef.current && document.activeElement?.tagName === 'IFRAME') {
      previewRef.current.focus();
    }
  }, []);

  const slidesNav = useCallback((n) => {
    if (!files || files.length <= 1) return;
    setActiveIndex(prevState => {
      let i = prevState + n;
      if (i === -1) i = files.length - 1;
      else if (i === files.length) i = 0;
      return i;
    });
  }, [files]);

  useEffect(() => {
    if (activeThumbRef.current) {
      activeThumbRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeIndex]);

  // For PDF: prevent iframe from stealing focus so Esc key always works
  // Scrolling still works via mouse wheel (hover-based, doesn't need focus)
  const isPdf = FILE.PDFFILES.test(currentFile?.url) || currentFile?.base64?.includes('application/pdf');
  useEffect(() => {
    if (!isPdf) return;

    const handleWindowBlur = () => {
      setTimeout(() => {
        if (document.activeElement?.tagName === 'IFRAME' && previewRef.current) {
          previewRef.current.focus();
        }
      }, 0);
    };

    window.addEventListener('blur', handleWindowBlur);
    return () => window.removeEventListener('blur', handleWindowBlur);
  }, [isPdf]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (previewRef.current) previewRef.current.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) { e.stopPropagation(); onClose?.(); }

      if (files?.length > 1) {
        if (e.key === 'ArrowLeft' || e.key === 'Left' || e.keyCode === 37) {
          slidesNav(-1);
        } else if (e.key === 'ArrowRight' || e.key === 'Right' || e.keyCode === 39) {
          slidesNav(1);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [onClose, files?.length, slidesNav]);

  return ReactDOM.createPortal(
    <div className="u_fv-preview u_animate u_fadeIn" ref={previewRef} tabIndex={-1} onMouseOver={handleMouseOver}>
      <FilePreviewHeader
        fileName={currentFile?.fileName}
        size={currentFile?.size}
        createdAt={currentFile?.createdAt}
        onDownload={handleDownload}
        onRotate={handleRotate}
        onClose={onClose}
        onDelete={onDelete}
        options={options}
        actions={actions}
        currentFile={currentFile}
        subheader={subheader && (typeof subheader === 'function' ? subheader(currentFile, activeIndex) : subheader)}
        hideDownload={hideDownload}
        hideFileInfo={hideFileInfo}
        allowRotation={FILE.IMAGES.test(`.${getExt(currentFile?.url)}`)}
      />
      <div className="u_fv-preview-body">
        {renderPreviewContent()}
        {files?.length > 1 && (
          <React.Fragment>
            <div className="u_prev-icon" onClick={() => slidesNav(-1)}>
              <Icon.LeftArrow size={35} fill="#FFFFFF" />
            </div>
            <div className="u_next-icon" onClick={() => slidesNav(1)}>
              <Icon.RightArrow size={35} fill="#FFFFFF" />
            </div>
          </React.Fragment>
        )}
      </div>
      {files?.length > 1 && (
        <div className="u_fv-thumbnails">
          <div className="u_fv-thumbnails-inner">
            {files.map((file, i) => {
              const isActive = i === activeIndex;
              const thumbUrl = file?.url ? (
                (file.url.startsWith('blob:') || file.url.startsWith('data:'))
                  ? file.url
                  : (FILE.IMAGES.test(file.url)
                      ? (getThumbor('100x100') + _S3_BASE_URL + file.url)
                      : null)
              ) : null;

              return (
                <div
                  key={file?._id || i}
                  className={`u_fv-thumbnails-item ${isActive ? 'u_active' : ''}`}
                  onClick={() => setActiveIndex(i)}
                  ref={isActive ? activeThumbRef : null}
                >
                  {thumbUrl ? (
                    <img src={thumbUrl} alt={file?.fileName} />
                  ) : (
                    <div className="u_fv-thumbnails-icon">
                      {getFrameworkFileIcon(file?.ext || file?.url?.split('.').pop(), 28)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
});

UFilePreview.propTypes = {
  file: PropTypes.shape({
    fileName: PropTypes.string,
    url: PropTypes.string,
    base64: PropTypes.string,
    size: PropTypes.number,
    createdAt: PropTypes.string
  }),
  files: PropTypes.array,
  initialIndex: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    icon: PropTypes.node,
    onClick: PropTypes.func
  })),
  subheader: PropTypes.node
};

UFilePreview.defaultProps = {
  file: {},
  files: null,
  initialIndex: 0
};

export default UFilePreview;

